import { useState, useEffect } from 'react'
import { useAuth } from './useAuth'
import { apiService } from '../services/api'
import { mapWithConcurrency } from '../lib/concurrency'

/**
 * What the totals below could NOT see.
 *
 * A partial total presented as a whole is the exact property #152 was fixed for
 * (and #159 after it): a confident number the user cannot tell is wrong.
 * Anything counted here must be shown to the user by the consuming dashboard.
 */
export interface DashboardGaps {
  /** Events whose analytics failed for a non-403 reason (429, 5xx, network). */
  events: number
  /** Event-list pages that failed, so an unknown number of events is missing. */
  listPages: number
}

/** A real event row, as returned by `GET /api/events`. */
export interface DashboardEvent {
  id: string
  name: string
  sport: string
  location: string
  startDate: string
  status: string
  /** `event._count.participants` from the server — not a client guess. */
  participantCount: number
}

/**
 * Participation buckets summed across the organizer's readable events, from
 * `GET /api/analytics/events/:id`. `null` for roles that never fetch analytics,
 * so a consumer cannot mistake "not fetched" for "nobody showed up".
 */
export interface OrganizerParticipation {
  total: number
  completed: number
  noShow: number
}

export interface DashboardStats {
  totalEvents: number
  activeEvents: number
  /**
   * `null` means "not readable", never "zero". Only an ADMIN has an endpoint
   * for a platform-wide athlete total; for every other role this is null so a
   * dashboard renders an em dash instead of asserting the org has no athletes.
   */
  totalAthletes: number | null
  /** `null` means "not readable", never "zero". See `totalAthletes`. */
  totalResults: number | null
  recentActivity: Array<{
    id: string
    type: 'event_created' | 'athlete_registered' | 'result_recorded'
    title: string
    description: string
    timestamp: string
  }>
  /** The real event rows behind `totalEvents`, for dashboards that list them. */
  events: DashboardEvent[]
  /** Organizer-only participation rollup; `null` for every other role. */
  participation: OrganizerParticipation | null
  gaps: DashboardGaps
}

export interface StatChange {
  value: string
  type: 'increase' | 'decrease' | 'neutral'
}

const ACTIVE_EVENT_STATUSES = new Set(['ACTIVE', 'SCHEDULED'])

const isActiveEvent = (status: string) => ACTIVE_EVENT_STATUSES.has(status)

const EVENTS_PAGE_SIZE = 100

const toDashboardEvent = (event: {
  id: string
  name: string
  sport: string
  location: string
  startDate: string
  status: string
  participantCount: number
}): DashboardEvent => ({
  id: event.id,
  name: event.name,
  sport: event.sport,
  location: event.location,
  startDate: event.startDate,
  status: event.status,
  participantCount: event.participantCount,
})

/** How many analytics requests may be in flight at once. See `mapWithConcurrency`. */
const ANALYTICS_CONCURRENCY = 4

export function useDashboardStats() {
  const { user } = useAuth()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  // Depend on the id and role, not the user object: a provider that returns a
  // fresh object each render would otherwise re-run the effect on every state
  // update and never settle.
  const userId = user?.id ?? null
  const role = user?.role

  useEffect(() => {
    if (!userId) {
      return
    }

    let cancelled = false

    const fetchStats = async () => {
      try {
        setLoading(true)
        setError(null)

        let dashboardStats: DashboardStats

        // Organizers ask the server for their own events (`mine`), rather than
        // probing analytics on every event on the platform and treating a 403
        // as "not mine" — one wasted request per foreign event, a console
        // error each, and a 100/minute rate limit (gate 5, 2026-10-23).
        const mineOnly = role === 'EVENT_ORGANIZER' ? { mine: true } : {}

        const fetchAllVisibleEvents = async () => {
          const firstPageResponse = await apiService.getEvents({
            page: 1,
            limit: EVENTS_PAGE_SIZE,
            ...mineOnly,
          })

          // Without page 1 there is no total and no baseline; nothing honest
          // can be rendered, so this is a hard failure rather than a gap.
          if (firstPageResponse.error) {
            throw new Error(firstPageResponse.error)
          }

          const firstPageData = firstPageResponse.data
          const firstPageEvents = firstPageData?.events || []
          const totalPages = firstPageData?.pagination?.pages || 0

          if (totalPages <= 1) {
            return {
              events: firstPageEvents,
              pagination: firstPageData?.pagination,
              failedPages: 0,
            }
          }

          const pageNumbers = Array.from({ length: totalPages - 1 }, (_, index) => index + 2)
          const remainingPageResponses = await mapWithConcurrency(
            pageNumbers,
            ANALYTICS_CONCURRENCY,
            page =>
              apiService
                .getEvents({ page, limit: EVENTS_PAGE_SIZE, ...mineOnly })
                .catch(() => ({ error: 'Network Error', data: undefined }))
          )

          const events = [...firstPageEvents]
          let failedPages = 0

          for (const response of remainingPageResponses) {
            // An `{ error }` body is a *resolved* promise. Spreading
            // `data?.events || []` here would turn a failed page into "that
            // page was empty" — a 250-event org would read as 200 events with
            // no indication anything was missing. This was left unfixed when
            // the same bug was closed in useAnalyticsSummary (#152); it is the
            // deferred half of that fix.
            if (response.error || !response.data) {
              failedPages++
              continue
            }
            events.push(...(response.data.events || []))
          }

          return { events, pagination: firstPageData?.pagination, failedPages }
        }

        if (role === 'EVENT_ORGANIZER') {
          const { events: visibleEvents, failedPages } = await fetchAllVisibleEvents()

          const analyticsResponses = await mapWithConcurrency(
            visibleEvents,
            ANALYTICS_CONCURRENCY,
            event =>
              apiService
                .getEventAnalytics(event.id)
                .catch(() => ({ error: 'Network Error', data: undefined, status: undefined }))
          )

          const organizerEvents: typeof visibleEvents = []
          let totalAthletes = 0
          let totalResults = 0
          let totalCompleted = 0
          let totalNoShow = 0
          let unreadableEvents = 0

          visibleEvents.forEach((event, index) => {
            const response = analyticsResponses[index]

            if (response?.data) {
              organizerEvents.push(event)

              const participation = response.data.participation
              if (participation) {
                totalAthletes +=
                  participation.registered +
                  participation.checkedIn +
                  participation.completed +
                  participation.noShow
                totalCompleted += participation.completed
                totalNoShow += participation.noShow
              }

              for (const drill of response.data.drillStats || []) {
                totalResults += drill.totalResults
              }
              return
            }

            // 403 is the backend declining to show an event this user may not
            // see — the scoping working as designed, not a gap in the data.
            // Anything else (429 from the shared 100/min rate limit, 5xx, 404,
            // network, or an absent status, so the fail-safe direction is
            // "surface it") means we do not know what this event holds, and
            // the user must be told.
            if (response?.status !== 403) {
              unreadableEvents++
            }
          })

          dashboardStats = {
            totalEvents: organizerEvents.length,
            activeEvents: organizerEvents.filter(event => isActiveEvent(event.status)).length,
            totalAthletes,
            totalResults,
            recentActivity: organizerEvents.map(event => ({
              id: event.id,
              type: 'event_created',
              title: event.name,
              description: `${event.sport} event at ${event.location}`,
              timestamp: event.startDate,
            })),
            events: organizerEvents.map(toDashboardEvent),
            participation: { total: totalAthletes, completed: totalCompleted, noShow: totalNoShow },
            gaps: { events: unreadableEvents, listPages: failedPages },
          }
        } else {
          // Pull the totals from the real list endpoints (each returns a
          // pagination.total). We previously called organization analytics with a
          // hardcoded 'sample-org-id', which 404s and left every count at 0 — and
          // there's no reliable org id in the user/scope context to pass here.
          //
          // Only ADMIN dashboards can safely use the unfiltered athlete/results
          // list totals. For other roles those endpoints expose global system
          // counts, which do not match "My Athletes"/organization-specific cards
          // — so those roles get `null` (rendered as an em dash) rather than a
          // `0` that reads as a real count of zero.
          const isAdmin = role === 'ADMIN'

          const eventsData = await fetchAllVisibleEvents()

          const [athletesRes, resultsRes] = await Promise.all([
            isAdmin
              ? apiService
                  .getAthletes({ limit: 1 })
                  .catch(() => ({ error: 'Network Error', data: undefined }))
              : Promise.resolve({ error: undefined, data: undefined }),
            isAdmin
              ? apiService
                  .getResults({ limit: 1 })
                  .catch(() => ({ error: 'Network Error', data: undefined }))
              : Promise.resolve({ error: undefined, data: undefined }),
          ])

          const athletesTotal = athletesRes.data?.pagination?.total
          const resultsTotal = resultsRes.data?.pagination?.total

          dashboardStats = {
            totalEvents: eventsData.pagination?.total || 0,
            activeEvents: eventsData.events.filter(e => isActiveEvent(e.status)).length,
            // An ADMIN whose athlete/result total failed to load gets null, not
            // 0: "we could not read it" and "there are none" must not render
            // the same way.
            totalAthletes: isAdmin ? (athletesTotal ?? null) : null,
            totalResults: isAdmin ? (resultsTotal ?? null) : null,
            recentActivity: eventsData.events.map(event => ({
              id: event.id,
              type: 'event_created',
              title: event.name,
              description: `${event.sport} event at ${event.location}`,
              timestamp: event.startDate,
            })),
            events: eventsData.events.map(toDashboardEvent),
            // No analytics are fetched for these roles, so there is no
            // participation rollup to report. `null`, not zeroes.
            participation: null,
            gaps: { events: 0, listPages: eventsData.failedPages },
          }
        }

        if (!cancelled) {
          setStats(dashboardStats)
        }
      } catch {
        if (!cancelled) {
          // No fallback figures. The previous code set every total to 0 here,
          // which rendered "0 events / 0 athletes" next to an error the stat
          // tiles never showed — an unreadable API must look unreadable.
          setStats(null)
          setError('Failed to load dashboard statistics')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchStats()

    return () => {
      cancelled = true
    }
  }, [userId, role, reloadToken])

  // Period-over-period change is not computed yet (the backend exposes no
  // previous-period snapshot). Return a neutral, empty delta rather than a
  // fabricated random percentage — showing invented metrics to a user is worse
  // than showing none. Wire this to real historical data when available.
  const getStatChange = (_value: number | null): StatChange => {
    return { value: '', type: 'neutral' }
  }

  const statsWithChanges = stats
    ? {
        ...stats,
        changes: {
          totalEvents: getStatChange(stats.totalEvents),
          activeEvents: getStatChange(stats.activeEvents),
          totalAthletes: getStatChange(stats.totalAthletes),
          totalResults: getStatChange(stats.totalResults),
        },
      }
    : null

  return {
    stats: statsWithChanges,
    loading,
    error,
    refetch: () => setReloadToken(token => token + 1),
  }
}
